import { defineStore } from 'pinia';
import { useAuthState } from '@/features/auth/composables/useAuthState';
import { storage } from '@/lib/storage';
import type { User, LoginFormData, RegisterFormData } from '@/types/auth';
import { useOrganizationsStore } from './organizations';

export const useAuthStore = defineStore('auth', () => {
  // Use the reusable auth state composable with app-specific storage integration
  const authState = useAuthState({
    requireVerification: true,
    persistToken: (token: string) => storage.setToken(token),
    persistRefreshToken: (token: string) => storage.setRefreshToken(token),
    getPersistedRefreshToken: () => storage.getRefreshToken(),
    clearPersistedData: () => storage.clearAuthData(),
    getPersistedToken: () => storage.getToken(),
    setPersistedUser: (user: User) => storage.setUserData(user),
  });

  // Application-specific login wrapper
  const login = async (formData: LoginFormData) => {
    const credentials = {
      email: formData.email,
      password: formData.password,
    };

    const result = await authState.login(credentials);
    if (authState.isAuthenticated) {
      const orgStore = useOrganizationsStore();
      await orgStore.initialize();
    }
    return result;
  };

  // Application-specific register wrapper
  const register = async (formData: RegisterFormData) => {
    const userData = {
      email: formData.email,
      password: formData.password,
      full_name: formData.full_name,
    };

    return authState.register(userData);
  };

  const logout = async () => {
    await authState.logout();
    const orgStore = useOrganizationsStore();
    orgStore.clear();
  };

  // Expose all auth state and actions
  return {
    // State from composable
    user: authState.user,
    token: authState.token,
    isLoading: authState.isLoading,
    error: authState.error,
    isInitialized: authState.isInitialized,

    // Computed getters from composable
    isAuthenticated: authState.isAuthenticated,
    isVerified: authState.isVerified,
    isActive: authState.isActive,
    canAccess: authState.canAccess,

    // Application-specific actions
    login,
    register,

    // Direct actions from composable
    forgotPassword: authState.forgotPassword,
    resetPassword: authState.resetPassword,
    verifyEmail: authState.verifyEmail,
    requestVerifyToken: authState.requestVerifyToken,
    getCurrentUser: authState.getCurrentUser,
    logout,
    clearError: authState.clearError,
    initialize: authState.initialize,
    refreshSession: authState.refreshSession,
  };
});
